import { getApi } from "../Services/apiService";

/** Same value as `HEADER_PREFERRED_FAMILY_SIDE_KEY` in `importTreeSideLock.js` (avoid circular imports). */
const HEADER_PREFERRED_FAMILY_SIDE_LS_KEY = "header_preferred_family_side";

/**
 * When GET /head-of-line is unscoped (`family_tree_id` empty), backend may need `type`:
 * Mother (API `Mother Side`, etc.) → 0, Father → 1. Returns null if unknown / unset.
 */
function getUnscopedHeadOfLineTypeFromStorage() {
  let preferredRaw = "";
  try {
    preferredRaw = (
      localStorage.getItem(HEADER_PREFERRED_FAMILY_SIDE_LS_KEY) || ""
    ).trim();
  } catch (_) {
    return null;
  }
  if (!preferredRaw) return null;
  const norm = String(preferredRaw)
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/\s+/g, " ");
  if (norm === "mothers side" || norm === "mother side" || norm === "mother") {
    return 0;
  }
  if (norm === "fathers side" || norm === "father side" || norm === "father") {
    return 1;
  }
  return null;
}

/**
 * Canonical family tree id: `loggedIn_user_role` (familyTreeId / family_tree_id),
 * then legacy `userDetail`, then top-level `familyTreeId`, for backward compatibility.
 */
function readFamilyTreeIdFromStorage() {
  try {
    const roleRaw = localStorage.getItem("loggedIn_user_role");
    if (roleRaw) {
      const role = JSON.parse(roleRaw);
      if (role?.familyTreeId || role?.family_tree_id) {
        return role.familyTreeId || role.family_tree_id;
      }
    }
  } catch (_) {
    /* ignore */
  }
  try {
    const userRaw = localStorage.getItem("userDetail");
    if (userRaw) {
      const user = JSON.parse(userRaw);
      if (user?.familyTreeId || user?.family_tree_id) {
        return user.familyTreeId || user.family_tree_id;
      }
    }
  } catch (_) {
    /* ignore */
  }
  try {
    const top = localStorage.getItem("familyTreeId");
    if (top != null && String(top).trim() !== "") return String(top).trim();
  } catch (_) {
    /* ignore */
  }
  return null;
}

/** @deprecated internal name — use getFamilyTreeIdFromStorage */
function getFamilyTreeIdForHeadOfLine() {
  return readFamilyTreeIdFromStorage();
}

export function getFamilyTreeIdFromStorage() {
  return readFamilyTreeIdFromStorage();
}

function readAdminDetailsFromRoleObject(raw, familyTreeId) {
  if (!raw || typeof raw !== "object") return null;
  const fid = raw.family_tree_id ?? raw.familyTreeId;
  const treeMatches =
    fid == null || fid === "" || String(fid) === String(familyTreeId);
  if (familyTreeId && raw[familyTreeId]?.admin_details) {
    return raw[familyTreeId].admin_details;
  }
  if (
    raw.admin_details &&
    typeof raw.admin_details === "object" &&
    (!familyTreeId || treeMatches)
  ) {
    return raw.admin_details;
  }
  return null;
}

/** Admin user id from an admin_details object (API may use id or user_id). */
export function getAdminUserIdFromDetails(adminDetails) {
  if (!adminDetails || typeof adminDetails !== "object") return null;
  const id =
    adminDetails.id ?? adminDetails.user_id ?? adminDetails.userId ?? null;
  return id != null && String(id).trim() !== "" ? id : null;
}

/**
 * Family admin display info from localStorage (`loggedIn_user_role`, legacy keys).
 * Matches header.jsx resolution order.
 */
export function getAdminDetailsFromStorage(familyTreeIdOverride) {
  const familyTreeId =
    familyTreeIdOverride ?? readFamilyTreeIdFromStorage() ?? "";
  const storageKeys = ["loggedIn_user_role", "userDetial", "userDetail"];
  for (const key of storageKeys) {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || "{}");
      const details = readAdminDetailsFromRoleObject(raw, familyTreeId);
      if (details) return details;
    } catch (_) {
      /* ignore */
    }
  }
  try {
    const standalone = JSON.parse(
      localStorage.getItem("admin_details") || "null"
    );
    if (standalone && typeof standalone === "object") return standalone;
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * Resolve family admin for Contact Admin / Admin Support chat: storage first, then APIs.
 */
export async function resolveAdminDetailsForSupport(familyTreeId) {
  const treeId = familyTreeId ?? readFamilyTreeIdFromStorage();
  if (!treeId) return null;

  let adminDetails = getAdminDetailsFromStorage(treeId);
  let adminId = getAdminUserIdFromDetails(adminDetails);
  if (adminId) {
    return { ...(adminDetails || {}), id: adminId };
  }

  try {
    await syncHeadOfLineRoleFromApi(treeId);
    adminDetails = getAdminDetailsFromStorage(treeId);
    adminId = getAdminUserIdFromDetails(adminDetails);
    if (adminId) {
      return { ...(adminDetails || {}), id: adminId };
    }
  } catch (e) {
    console.warn("resolveAdminDetailsForSupport head-of-line:", e);
  }

  try {
    const response = await getApi("/my-family-trees?type=1");
    const list = Array.isArray(response?.data) ? response.data : [];
    const found = list.find((t) => String(t.id) === String(treeId));
    if (found) {
      const creatorId =
        found.creator_user_id ??
        found.creator_id ??
        found.user_id ??
        found.created_by_user_id ??
        found.created_by ??
        null;
      if (creatorId != null && String(creatorId).trim() !== "") {
        return {
          id: creatorId,
          name:
            found.creator_name ||
            adminDetails?.name ||
            "Admin",
        };
      }
    }
  } catch (e) {
    console.warn("resolveAdminDetailsForSupport my-family-trees:", e);
  }

  const adminName = (adminDetails?.name || "").trim();
  if (adminName) {
    try {
      const response = await getApi(
        `/family-members-for-invitation?family_tree_id=${encodeURIComponent(treeId)}`
      );
      const members = response?.data?.members;
      if (Array.isArray(members)) {
        const normalizedAdminName = adminName.toLowerCase();
        const adminFirstName = normalizedAdminName.split(/\s+/)[0] || "";
        const match = members.find((m) => {
          const full = (
            m.name ||
            [m.first_name, m.last_name].filter(Boolean).join(" ") ||
            ""
          )
            .trim()
            .toLowerCase();
          const first = (m.first_name || "").trim().toLowerCase();
          return (
            (full && full === normalizedAdminName) ||
            (adminFirstName && first === adminFirstName)
          );
        });
        const userId = match?.user_id;
        if (userId != null && String(userId).trim() !== "") {
          return {
            id: userId,
            name: adminName || match.name || match.first_name || "Admin",
          };
        }
      }
    } catch (e) {
      console.warn("resolveAdminDetailsForSupport family-members:", e);
    }
  }

  return null;
}

/**
 * Persist tree id on `loggedIn_user_role` (and top-level `familyTreeId` for legacy readers).
 * Does not write to `userDetail`.
 */
export function setFamilyTreeIdInLoggedInRole(treeId) {
  if (treeId == null || String(treeId).trim() === "") return;
  const id = String(treeId).trim();
  try {
    const raw = localStorage.getItem("loggedIn_user_role");
    const role = raw ? JSON.parse(raw) : {};
    const next = {
      ...role,
      familyTreeId: id,
      family_tree_id: id,
    };
    localStorage.setItem("loggedIn_user_role", JSON.stringify(next));
    localStorage.setItem("familyTreeId", id);
    window.dispatchEvent(new Event("loggedInUserRoleUpdated"));
    window.dispatchEvent(new Event("localStorageChange"));
  } catch (e) {
    console.warn("setFamilyTreeIdInLoggedInRole:", e);
  }
}

/** Remove tree ids from role + top-level; optionally strip legacy keys from userDetail. */
export function clearFamilyTreeIdsFromStorage() {
  try {
    const raw = localStorage.getItem("loggedIn_user_role");
    if (raw) {
      const role = JSON.parse(raw);
      if (role && typeof role === "object") {
        delete role.familyTreeId;
        delete role.family_tree_id;
        localStorage.setItem("loggedIn_user_role", JSON.stringify(role));
      }
    }
    localStorage.removeItem("familyTreeId");
    const uRaw = localStorage.getItem("userDetail");
    if (uRaw) {
      const u = JSON.parse(uRaw);
      if (u && typeof u === "object") {
        delete u.family_tree_id;
        delete u.familyTreeId;
        localStorage.setItem("userDetail", JSON.stringify(u));
      }
    }
    window.dispatchEvent(new Event("loggedInUserRoleUpdated"));
    window.dispatchEvent(new Event("localStorageChange"));
  } catch (_) {
    /* ignore */
  }
}

/**
 * Fetches head-of-line role (is_admin, is_subscription, …) and persists to localStorage.
 * Runs even when no tree id is known (GET …?family_tree_id=), matching subscription/plans.
 * @param {string|null|undefined} familyTreeIdOverride - If set, use this tree id instead of reading from storage.
 * @param {{ omitTreeIdsFromResponseWhenUnscoped?: boolean }} [options] - When the request is unscoped (`?family_tree_id=`) and this is true, do not copy tree ids from the response into storage (avoids re-applying a default tree after the user picked a side that has no tree yet).
 * @returns {Promise<object|null>} Parsed role object or null on failure
 */
export async function syncHeadOfLineRoleFromApi(familyTreeIdOverride, options = {}) {
  try {
    let rawId =
      familyTreeIdOverride != null && String(familyTreeIdOverride).trim() !== ""
        ? String(familyTreeIdOverride).trim()
        : getFamilyTreeIdForHeadOfLine();
    const familyTreeId =
      rawId != null && String(rawId).trim() !== "" ? String(rawId).trim() : "";

    const omitTreeIdsFromResponseWhenUnscoped =
      Boolean(options?.omitTreeIdsFromResponseWhenUnscoped) && !familyTreeId;

    // Same contract as getSubscriptionPlansApiUrl(): call with empty family_tree_id when none.
    let url = familyTreeId
      ? `/head-of-line/${encodeURIComponent(familyTreeId)}`
      : `/head-of-line?family_tree_id=`;
    if (!familyTreeId) {
      const unscopedType = getUnscopedHeadOfLineTypeFromStorage();
      if (unscopedType === 0 || unscopedType === 1) {
        url += `&type=${unscopedType}`;
      }
    }

    const response = await getApi(url);
    let fullRoleData = response?.data ?? {};
    if (
      fullRoleData &&
      typeof fullRoleData === "object" &&
      fullRoleData.data != null &&
      typeof fullRoleData.data === "object" &&
      !Array.isArray(fullRoleData.data)
    ) {
      fullRoleData = fullRoleData.data;
    }
    let resolvedTreeId = "";
    if (omitTreeIdsFromResponseWhenUnscoped) {
      resolvedTreeId = (familyTreeId && familyTreeId.trim()) || "";
    } else {
      resolvedTreeId =
        (familyTreeId && familyTreeId.trim()) ||
        fullRoleData?.familyTreeId ||
        fullRoleData?.family_tree_id ||
        "";
    }
    const merged = {
      ...fullRoleData,
      ...(resolvedTreeId
        ? { familyTreeId: String(resolvedTreeId), family_tree_id: String(resolvedTreeId) }
        : {}),
    };
    if (omitTreeIdsFromResponseWhenUnscoped) {
      delete merged.familyTreeId;
      delete merged.family_tree_id;
    }
    localStorage.setItem("loggedIn_user_role", JSON.stringify(merged));
    if (resolvedTreeId) {
      localStorage.setItem("familyTreeId", String(resolvedTreeId));
    } else {
      localStorage.removeItem("familyTreeId");
    }
    const isAdmin = merged?.is_admin ?? true;
    localStorage.setItem("is_admin", JSON.stringify(isAdmin));
    window.dispatchEvent(new Event("loggedInUserRoleUpdated"));
    // Header and other UIs listen for this to refresh admin display after correct role/admin_details load.
    window.dispatchEvent(new Event("localStorageChange"));
    return merged;
  } catch (e) {
    console.warn("syncHeadOfLineRoleFromApi:", e?.message || e);
    return null;
  }
}
